import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";
import { Pill } from "@/components/shared/Pill";
import Pagination, { usePagination } from "@/components/shared/Pagination";

// Chips compacts pour la colonne "Services"
function ServiceChips({ services, early, late }) {
  const items = [];
  if (services?.cleaning) items.push({ k: "clean", label: "🧹 Ménage", tone: "emerald" });
  const ks = services?.linen_kits_simple || 0;
  const kd = services?.linen_kits_double || 0;
  if (ks > 0) items.push({ k: "ks", label: `🛏️ ${ks} kit${ks > 1 ? "s" : ""} simple`, tone: "violet" });
  if (kd > 0) items.push({ k: "kd", label: `🛏️ ${kd} kit${kd > 1 ? "s" : ""} double`, tone: "violet" });
  const bs = services?.beds_made_simple || 0;
  const bd = services?.beds_made_double || 0;
  if (bs > 0) items.push({ k: "bs", label: `🛌 ${bs} lit${bs > 1 ? "s" : ""} fait simple`, tone: "violet" });
  if (bd > 0) items.push({ k: "bd", label: `🛌 ${bd} lit${bd > 1 ? "s" : ""} fait double`, tone: "violet" });
  if (services?.hygiene_kit) items.push({ k: "hy", label: "🧴 Hygiène", tone: "amber" });
  if (services?.degreasing) items.push({ k: "dg", label: "🧽 Dégraissage", tone: "amber" });
  if (services?.welcome_basket && services.welcome_basket !== "none") {
    items.push({ k: "wb", label: `🧺 Panier ${services.welcome_basket}`, tone: "amber" });
  }
  if (early?.approved) items.push({ k: "ea", label: "⬅️ Arrivée anticipée", tone: "slate" });
  if (late?.approved) items.push({ k: "ld", label: "➡️ Départ tardif", tone: "slate" });
  if (!items.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it) => (
        <Pill key={it.k} tone={it.tone}>{it.label}</Pill>
      ))}
    </div>
  );
}

export default function AdminStaysSearch() {
  const [search, setSearch] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stayType, setStayType] = useState("");
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [laundryInterventions, setLaundryInterventions] = useState([]);
  const [sortKey, setSortKey] = useState("start_date");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  // ─── Détail séjour + édition (Dialog "Détail du séjour") ──────────────
  // detailRow : la row cliquée (référence, pas de re-render)
  // detailForm : brouillon complet (dates + tenant + services + notes)
  const [detailRow, setDetailRow] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailSaving, setDetailSaving] = useState(false);

  // Détection de conflit temps-réel sur les dates
  const [conflictState, setConflictState] = useState({
    checking: false,
    has_hard_conflict: false,
    has_soft_conflict: false,
    hard: [],
    soft: [],
  });

  const openDetail = (row) => {
    const s = row.services || {};
    const t = row.tenant || {};
    setDetailRow(row);
    setDetailForm({
      // Dates
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      // Tenant
      tenant_name: t.name || "",
      tenant_email: t.email || "",
      tenant_phone: t.phone || "",
      tenant_nationality: t.nationality || "",
      tenant_people_count: Number(t.people_count ?? 1),
      // Services
      cleaning: !!s.cleaning,
      degreasing: !!s.degreasing,
      linen_kits_simple: Number(s.linen_kits_simple || 0),
      linen_kits_double: Number(s.linen_kits_double || 0),
      beds_made_simple: Number(s.beds_made_simple || 0),
      beds_made_double: Number(s.beds_made_double || 0),
      hygiene_kit: !!s.hygiene_kit,
      welcome_basket: s.welcome_basket || "none",
      puericulture_items: Array.isArray(s.puericulture_items) ? [...s.puericulture_items] : [],
      notes_planning: row.notes_planning || "",
      notes_facturation: row.notes_facturation || "",
    });
    setConflictState({ checking: false, has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [] });
    setNotesFactSuggested(false);
  };

  const closeDetail = () => {
    setDetailRow(null);
    setDetailForm(null);
    setConflictState({ checking: false, has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [] });
    setNotesFactSuggested(false);
  };

  // Plafonne les lits faits par le nombre de kits correspondant (comme OwnerApp)
  const setKitSimple = (v) => {
    const n = Math.max(0, Number(v || 0));
    setDetailForm((f) => ({
      ...f,
      linen_kits_simple: n,
      beds_made_simple: Math.min(Number(f?.beds_made_simple || 0), n),
    }));
  };
  const setKitDouble = (v) => {
    const n = Math.max(0, Number(v || 0));
    setDetailForm((f) => ({
      ...f,
      linen_kits_double: n,
      beds_made_double: Math.min(Number(f?.beds_made_double || 0), n),
    }));
  };
  const togglePuericulture = (item, checked) => {
    setDetailForm((f) => {
      const cur = new Set(f?.puericulture_items || []);
      if (checked) cur.add(item);
      else cur.delete(item);
      return { ...f, puericulture_items: Array.from(cur) };
    });
  };

  // ── Détection de conflit avec debounce quand les dates changent ────────
  const datesChanged = detailRow && detailForm && (
    detailForm.start_date !== detailRow.start_date ||
    detailForm.end_date !== detailRow.end_date
  );
  useEffect(() => {
    if (!detailRow || !detailForm) return;
    if (!datesChanged) {
      setConflictState({ checking: false, has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [] });
      return;
    }
    if (!detailForm.start_date || !detailForm.end_date) return;
    if (detailForm.end_date <= detailForm.start_date) {
      setConflictState({ checking: false, has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [] });
      return;
    }
    let cancelled = false;
    setConflictState((c) => ({ ...c, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/stays/${detailRow.id}/conflicts`, {
          params: { start_date: detailForm.start_date, end_date: detailForm.end_date },
        });
        if (!cancelled) setConflictState({ checking: false, ...res.data });
      } catch (e) {
        if (!cancelled) {
          setConflictState({ checking: false, has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [] });
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailForm?.start_date, detailForm?.end_date, detailRow?.id]);

  // AJOUT D — état du dialog "Envoyer email au propriétaire ?"
  const [emailPromptRow, setEmailPromptRow] = useState(null); // {row, payload}

  // AJOUT E — Annulation directe d'un séjour par l'admin
  //   Étape 1 : confirmation (AlertDialog) — avec warning si séjour facturé
  //   Étape 2 : question email (Dialog) — Oui/Non
  const [cancelConfirmRow, setCancelConfirmRow] = useState(null); // row à annuler
  const [cancelEmailPromptRow, setCancelEmailPromptRow] = useState(null); // row → étape 2
  const [cancelling, setCancelling] = useState(false);

  const openCancelConfirm = () => {
    if (!detailRow) return;
    setCancelConfirmRow(detailRow);
  };

  const performCancel = async (sendEmail) => {
    const row = cancelEmailPromptRow;
    if (!row) return;
    setCancelling(true);
    try {
      const res = await api.post(
        `/admin/stays/${row.id}/cancel?send_email_to_owner=${sendEmail ? "true" : "false"}`
      );
      const invWarn = res.data?.invoice_warnings || [];
      if (invWarn.length) {
        toast({
          title: "Séjour annulé — factures inchangées",
          description: `${invWarn.length} facture(s) existante(s) ne sont pas modifiées : ${invWarn.map((w) => w.invoice_number).filter(Boolean).join(", ") || "n° non renseigné"}.`,
        });
      } else if (sendEmail) {
        toast({
          title: res.data?.email_sent ? "Séjour annulé — email envoyé" : "Séjour annulé",
          description: res.data?.email_sent
            ? "Le propriétaire a reçu l'email d'annulation."
            : "Annulation enregistrée. (Aucun email — SMTP absent ou destinataire manquant.)",
        });
      } else {
        toast({ title: "Séjour annulé" });
      }
      // Retirer localement le séjour annulé (il est filtré côté serveur à la prochaine requête)
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setCancelEmailPromptRow(null);
      setCancelConfirmRow(null);
      closeDetail();
    } catch (e) {
      toast({ title: "Annulation impossible", description: formatError(e), variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  // ── Séjour facturé (AJOUT 3) : dates + services verrouillés ────────────
  const isInvoiced = !!detailRow?.is_invoiced;

  // ── AJOUT G : auto-remplissage de notes_facturation à la première modif
  //    d'un séjour couvert par une facture. Ne s'exécute qu'UNE fois par ouverture
  //    de modale et uniquement si le champ est vide (n'écrase jamais une note).
  const [notesFactSuggested, setNotesFactSuggested] = useState(false);
  useEffect(() => {
    if (!detailRow || !detailForm || !isInvoiced || notesFactSuggested) return;
    if ((detailForm.notes_facturation || "").trim()) return; // déjà rempli → ne rien faire
    const origServices = detailRow.services || {};
    const currentServices = {
      cleaning: !!detailForm.cleaning,
      degreasing: !!detailForm.degreasing,
      linen_kits_simple: Number(detailForm.linen_kits_simple || 0),
      linen_kits_double: Number(detailForm.linen_kits_double || 0),
      beds_made_simple: Number(detailForm.beds_made_simple || 0),
      beds_made_double: Number(detailForm.beds_made_double || 0),
      hygiene_kit: !!detailForm.hygiene_kit,
      welcome_basket: detailForm.welcome_basket || "none",
      puericulture_items: (detailForm.puericulture_items || []).slice().sort().join(","),
    };
    const orig = {
      cleaning: !!origServices.cleaning,
      degreasing: !!origServices.degreasing,
      linen_kits_simple: Number(origServices.linen_kits_simple || 0),
      linen_kits_double: Number(origServices.linen_kits_double || 0),
      beds_made_simple: Number(origServices.beds_made_simple || 0),
      beds_made_double: Number(origServices.beds_made_double || 0),
      hygiene_kit: !!origServices.hygiene_kit,
      welcome_basket: origServices.welcome_basket || "none",
      puericulture_items: (origServices.puericulture_items || []).slice().sort().join(","),
    };
    const datesChanged = (
      detailForm.start_date !== (detailRow.start_date || "") ||
      detailForm.end_date !== (detailRow.end_date || "")
    );
    const servicesChanged = JSON.stringify(currentServices) !== JSON.stringify(orig);
    if (datesChanged || servicesChanged) {
      const today = new Date().toISOString().slice(0, 10);
      const suggestion = `[${today}] Modification effectuée après facturation existante — à vérifier à la prochaine facture.`;
      setDetailForm((f) => ({ ...f, notes_facturation: suggestion }));
      setNotesFactSuggested(true);
    }
  }, [detailRow, detailForm, isInvoiced, notesFactSuggested]);

  // ── Validations formulaire ────────────────────────────────────────────
  // NB : la validation de format email a été RETIRÉE du canSave — l'email
  // reste un champ libre (d'anciens séjours peuvent contenir "no email",
  // des placeholders, etc.). Le backend l'accepte tel quel.
  const peopleCountValid = Number(detailForm?.tenant_people_count || 0) >= 1;
  const datesValid = !!(detailForm?.start_date && detailForm?.end_date &&
    detailForm.end_date > detailForm.start_date);
  const canSave = !detailSaving && peopleCountValid && datesValid &&
    !conflictState.has_hard_conflict && !conflictState.checking;

  const saveDetail = async () => {
    if (!detailRow || !detailForm || !canSave) return;
    // AJOUT D : au lieu d'envoyer directement, on demande confirmation d'email
    setEmailPromptRow({ row: detailRow, form: { ...detailForm } });
  };

  const performSave = async (sendEmail) => {
    const ctx = emailPromptRow;
    if (!ctx) return;
    setDetailSaving(true);
    try {
      const df = ctx.form;
      const kitsS = Math.max(0, Number(df.linen_kits_simple || 0));
      const kitsD = Math.max(0, Number(df.linen_kits_double || 0));
      const bedsS = Math.min(Math.max(0, Number(df.beds_made_simple || 0)), kitsS);
      const bedsD = Math.min(Math.max(0, Number(df.beds_made_double || 0)), kitsD);
      const services = {
        cleaning: !!df.cleaning,
        degreasing: !!df.degreasing,
        linen_kits_simple: kitsS,
        linen_kits_double: kitsD,
        beds_made_simple: bedsS,
        beds_made_double: bedsD,
        hygiene_kit: !!df.hygiene_kit,
        welcome_basket: df.welcome_basket || "none",
        puericulture_items: Array.isArray(df.puericulture_items) ? df.puericulture_items : [],
      };
      const tenant = {
        name: (df.tenant_name || "").trim(),
        email: (df.tenant_email || "").trim() || null,
        phone: (df.tenant_phone || "").trim() || null,
        nationality: (df.tenant_nationality || "").trim() || null,
        people_count: Math.max(1, Number(df.tenant_people_count || 1)),
      };
      const payload = {
        tenant,
        notes_planning: (df.notes_planning || "").trim() || null,
        notes_facturation: (df.notes_facturation || "").trim() || null,
        start_date: df.start_date,
        end_date: df.end_date,
        services,
      };
      const res = await api.put(`/admin/stays/${ctx.row.id}?send_email_to_owner=${sendEmail ? "true" : "false"}`, payload);

      const warnings = res.data?.warnings || [];
      if (warnings.length) {
        toast({
          title: "Séjour mis à jour — factures inchangées",
          description: `${warnings.length} facture(s) couvrent l'ancienne ou la nouvelle période et n'ont pas été modifiées : ${warnings.map((w) => w.invoice_number).filter(Boolean).join(", ") || "n° non renseigné"}.`,
        });
      } else if (sendEmail) {
        toast({
          title: res.data?.email_sent ? "Séjour mis à jour — email envoyé" : "Séjour mis à jour",
          description: res.data?.email_sent ? "Le propriétaire a reçu un email de confirmation." : "Modification enregistrée. (Aucun email — SMTP absent ou destinataire manquant.)",
        });
      } else {
        toast({ title: "Séjour mis à jour" });
      }

      setRows((prev) => prev.map((r) => (r.id === ctx.row.id ? {
        ...r,
        start_date: df.start_date,
        end_date: df.end_date,
        tenant,
        services,
        notes_planning: (df.notes_planning || "").trim(),
        notes_facturation: (df.notes_facturation || "").trim(),
      } : r)));
      setEmailPromptRow(null);
      closeDetail();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setDetailSaving(false);
    }
  };

  // Liste des logements filtrée selon le propriétaire sélectionné
  const propertiesForSelect = useMemo(() => {
    if (!ownerId) return properties;
    return properties.filter((p) => p.owner_id === ownerId);
  }, [properties, ownerId]);

  const _buildParams = () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (ownerId) params.owner_id = ownerId;
    if (propertyId) params.property_id = propertyId;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (stayType) params.stay_type = stayType;
    return params;
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      const params = _buildParams();
      const [res, laundryRes] = await Promise.all([
        api.get("/admin/stays/search", { params }),
        (async () => {
          // Charger les interventions confirmées dans la période Journal si `from` et `to` sont renseignés
          if (!params.from_date || !params.to_date) return { data: [] };
          const p = { from_date: params.from_date, to_date: params.to_date };
          if (params.property_id) p.property_id = params.property_id;
          try { return await api.get("/admin/laundry-interventions", { params: p }); } catch { return { data: [] }; }
        })(),
      ]);
      setRows(res.data || []);
      setLaundryInterventions(laundryRes.data || []);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/stays/export.xlsx", { params: _buildParams(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.download = `reservations_bdm_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Export Excel téléchargé", description: "Les réservations filtrées ont été exportées." });
    } catch (e) {
      toast({ title: "Export impossible", description: formatError(e), variant: "destructive" });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [oRes, pRes] = await Promise.all([
          api.get("/admin/owners", { params: { include_archived: true } }),
          api.get("/admin/properties", { params: { include_archived: true } }),
        ]);
        setOwners(oRes.data || []);
        setProperties(pRes.data || []);
      } catch (e) {
        console.warn("AdminStaysSearch refs load failed", e);
      }
      loadResults();
    })();
  }, []);

  // Reset propertyId si le owner change et que le logement ne correspond plus
  useEffect(() => {
    if (propertyId && ownerId) {
      const p = properties.find((x) => x.id === propertyId);
      if (p && p.owner_id !== ownerId) setPropertyId("");
    }
  }, [ownerId, propertyId, properties]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    copy.sort((a, b) => ((a[sortKey] || "") > (b[sortKey] || "") ? dir : -dir));
    return copy;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const filterKey = `${search}|${ownerId}|${propertyId}|${fromDate}|${toDate}|${stayType}|${rows.length}`;
  const { page, setPage, pageSize, setPageSize, paginated, total } = usePagination(sortedRows, {
    defaultPageSize: 50,
    resetKey: filterKey,
  });

  return (
    <div data-testid="admin-stays-search" className="space-y-4">
      <Card data-testid="admin-stays-search-filters-card" className="border-white/10 bg-white/[0.08]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-7">
            <div className="space-y-1 md:col-span-2">
              <Label>Locataire</Label>
              <Input
                data-testid="admin-stays-search-name"
                placeholder="Nom du locataire…"
                className="border-white/10 bg-white/[0.08]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadResults(); }}
              />
            </div>
            <div className="space-y-1">
              <Label>Propriétaire</Label>
              <select
                data-testid="admin-stays-search-owner-filter"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
              >
                <option value="">Tous les propriétaires</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.display_name || `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.billing_email || o.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Logement</Label>
              <select
                data-testid="admin-stays-search-property-filter"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              >
                <option value="">Tous les logements</option>
                {propertiesForSelect.map((p) => (
                  <option key={p.id} value={p.id}>{p.short_name || p.id}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Date début</Label>
              <Input
                data-testid="admin-stays-search-from-date"
                type="date"
                className="border-white/10 bg-white/[0.08]"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Date fin</Label>
              <Input
                data-testid="admin-stays-search-to-date"
                type="date"
                className="border-white/10 bg-white/[0.08]"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <select
                data-testid="admin-stays-search-type-filter"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={stayType}
                onChange={(e) => setStayType(e.target.value)}
              >
                <option value="">Tous</option>
                <option value="tenant">Locataire</option>
                <option value="owner">Propriétaire</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            <Button
              data-testid="admin-stays-search-filter-button"
              className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
              onClick={loadResults}
              disabled={loading}
            >
              {loading ? "..." : "Filtrer"}
            </Button>
            <Button
              data-testid="admin-stays-search-export-xlsx-button"
              className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400"
              onClick={exportXlsx}
              title="Exporter les réservations filtrées en Excel"
            >
              📊 Exporter Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="admin-stays-search-list-card" className="border-white/10 bg-white/[0.08]">
        <CardContent className="pt-6">
          <div className="rounded-xl border border-white/10 bg-white/[0.08] overflow-x-auto">
            <Table data-testid="admin-stays-search-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Logement</TableHead>
                  <TableHead className="w-[160px]">Propriétaire</TableHead>
                  <TableHead className="w-[110px]">Type</TableHead>
                  <TableHead className="w-[180px]">Locataire</TableHead>
                  <TableHead className="w-[90px]">Nationalité</TableHead>
                  <TableHead
                    data-testid="admin-stays-search-sort-arrival"
                    className="w-[120px] cursor-pointer select-none hover:text-slate-100"
                    onClick={() => toggleSort("start_date")}
                  >
                    Arrivée {sortKey === "start_date" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </TableHead>
                  <TableHead
                    data-testid="admin-stays-search-sort-departure"
                    className="w-[120px] cursor-pointer select-none hover:text-slate-100"
                    onClick={() => toggleSort("end_date")}
                  >
                    Départ {sortKey === "end_date" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </TableHead>
                  <TableHead className="w-[70px]">Nuits</TableHead>
                  <TableHead className="w-[80px]">Personnes</TableHead>
                  <TableHead className="min-w-[260px]">Services</TableHead>
                  <TableHead className="min-w-[160px]">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((r, idx) => (
                  <TableRow
                    data-testid={`admin-stays-search-row-clickable-${idx}`}
                    key={r.id || idx}
                    className="align-top cursor-pointer hover:bg-white/[0.04] transition-colors"
                    onClick={() => openDetail(r)}
                  >
                    <TableCell className="text-sm text-slate-200">{r.property_short_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-300">{r.owner_name || "—"}</TableCell>
                    <TableCell>
                      {r.stay_type === "owner"
                        ? <Pill tone="slate">Propriétaire</Pill>
                        : <Pill tone="sky">Locataire</Pill>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-200">
                      <div className="font-medium flex items-center gap-1.5">
                        {r.notes_facturation && (
                          <span
                            data-testid={`admin-stays-search-row-notes-facturation-${idx}`}
                            title={`💰 À régulariser : ${r.notes_facturation}`}
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/25 text-amber-200 text-[10px] leading-none border border-amber-500/40"
                          >
                            💰
                          </span>
                        )}
                        <span>{r.tenant?.name || "—"}</span>
                      </div>
                      {r.tenant?.email && <div className="text-xs text-slate-400 break-all">{r.tenant.email}</div>}
                      {r.tenant?.phone && <div className="text-xs text-slate-400">{r.tenant.phone}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-300">{r.tenant?.nationality || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-200 whitespace-nowrap">{formatDateEU(r.start_date)}</TableCell>
                    <TableCell className="text-sm text-slate-200 whitespace-nowrap">{formatDateEU(r.end_date)}</TableCell>
                    <TableCell className="text-sm text-slate-200 text-center">{r.nuits || 0}</TableCell>
                    <TableCell className="text-sm text-slate-200 text-center">{r.tenant?.people_count ?? "—"}</TableCell>
                    <TableCell>
                      <ServiceChips services={r.services} early={r.early_arrival} late={r.late_departure} />
                    </TableCell>
                    <TableCell className="text-xs text-slate-300 max-w-xs whitespace-pre-wrap">{r.notes_planning || "—"}</TableCell>
                  </TableRow>
                ))}
                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-slate-300">
                      Aucune réservation trouvée pour ces critères.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testIdPrefix="admin-stays-search-pagination"
          />
        </CardContent>
      </Card>

      {/* AJOUT iter51 — Section Interventions Blanchisserie/Dégraissage confirmées */}
      {laundryInterventions.length > 0 && (
        <Card data-testid="admin-stays-search-laundry-card" className="border-emerald-500/30 bg-emerald-500/[0.03]">
          <CardHeader>
            <CardTitle className="text-slate-100 text-base flex items-center gap-2">
              🧺 Interventions Blanchisserie/Dégraissage
              <span className="text-xs text-slate-400 font-normal">({laundryInterventions.length} confirmée(s) dans la période)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {laundryInterventions.map((iv) => {
              const typeLabel = iv.request_type === "degreasing" ? "Dégraissage"
                : iv.request_type === "laundry_full" ? "Blanchisserie complète"
                : "Blanchisserie ponctuelle";
              return (
                <div
                  key={iv.id}
                  data-testid={`admin-stays-search-laundry-row-${iv.id}`}
                  className="rounded-md border border-emerald-500/30 bg-white/[0.03] p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                        {iv.property_short_name || iv.property_id}
                        <span className="rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 px-2 py-0.5 text-[10px] uppercase">{typeLabel}</span>
                        <span className="text-xs text-slate-400">le <b>{iv.confirmed_date}</b></span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">Propriétaire : {iv.owner_name}</div>
                      {iv.request_type === "laundry_full" && iv.articles?.length > 0 && (
                        <div className="text-xs text-slate-300 mt-1">
                          <b>Articles :</b> {iv.articles.map(a => `${a.name} × ${a.qty}`).join(", ")}
                        </div>
                      )}
                      {iv.request_type === "laundry_punctual" && iv.punctual_description && (
                        <div className="text-xs text-slate-300 mt-1"><b>Détail :</b> {iv.punctual_description}</div>
                      )}
                    </div>
                    {iv.final_price_ttc != null && (
                      <div className="text-sm font-semibold text-emerald-300">{Number(iv.final_price_ttc).toFixed(2)}€</div>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ─── Dialog "Détail du séjour" (édition des services uniquement) ─── */}
      <Dialog open={!!detailRow} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent
          data-testid="admin-stay-detail-dialog"
          className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[720px] max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle data-testid="admin-stay-detail-title">Détail du séjour</DialogTitle>
          </DialogHeader>

          {detailRow && detailForm && (
            <div className="space-y-4">
              {/* ── Contexte lecture seule : logement + propriétaire + type ─── */}
              <div
                data-testid="admin-stay-detail-readonly-block"
                className="rounded-xl border border-white/10 bg-white/[0.05] p-3 space-y-2 text-sm"
              >
                <div className="grid grid-cols-[130px_1fr] gap-y-1">
                  <div className="text-slate-400">Logement</div>
                  <div data-testid="admin-stay-detail-property" className="text-slate-100 font-medium">{detailRow.property_short_name || "—"}</div>
                  <div className="text-slate-400">Propriétaire</div>
                  <div data-testid="admin-stay-detail-owner" className="text-slate-100">{detailRow.owner_name || "—"}</div>
                  <div className="text-slate-400">Type</div>
                  <div data-testid="admin-stay-detail-type">
                    {detailRow.stay_type === "owner"
                      ? <Pill tone="slate">Propriétaire</Pill>
                      : <Pill tone="sky">Locataire</Pill>}
                  </div>
                </div>

                {(detailRow.early_arrival?.approved || detailRow.late_departure?.approved) && (
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                    {detailRow.early_arrival?.approved && (
                      <Pill tone="slate" testId="admin-stay-detail-early-pill">⬅️ Arrivée anticipée</Pill>
                    )}
                    {detailRow.late_departure?.approved && (
                      <Pill tone="slate" testId="admin-stay-detail-late-pill">➡️ Départ tardif</Pill>
                    )}
                    <span className="text-xs text-slate-400" data-testid="admin-stay-detail-early-late-hint">
                      (géré via l&apos;onglet Demandes)
                    </span>
                  </div>
                )}
              </div>

              {/* Bandeau informatif si une facture couvre déjà la période
                  Le champ `is_invoiced` reste calculé côté serveur (chevauchement de dates),
                  mais il n'entraîne PLUS aucun verrouillage UI/serveur — c'est un simple rappel. */}
              {isInvoiced && (
                <div
                  data-testid="admin-stay-detail-invoiced-banner"
                  className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100"
                >
                  🔒 Une facture existe déjà sur cette période — vérifiez qu&apos;elle reflète bien vos modifications.
                </div>
              )}

              {/* ── Dates éditables + bandeaux de conflit ────────────────── */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-100">Dates du séjour</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-start-date-label" className="text-xs text-slate-300">Arrivée</Label>
                    <Input
                      data-testid="admin-stay-detail-start-date"
                      type="date"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.start_date}
                      onChange={(e) => setDetailForm((f) => ({ ...f, start_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-end-date-label" className="text-xs text-slate-300">Départ</Label>
                    <Input
                      data-testid="admin-stay-detail-end-date"
                      type="date"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.end_date}
                      onChange={(e) => setDetailForm((f) => ({ ...f, end_date: e.target.value }))}
                    />
                  </div>
                </div>
                {!datesValid && (
                  <div data-testid="admin-stay-detail-dates-invalid" className="text-xs text-rose-300">
                    La date de départ doit être postérieure à la date d&apos;arrivée.
                  </div>
                )}
                {conflictState.checking && datesChanged && (
                  <div data-testid="admin-stay-detail-conflict-checking" className="text-xs text-slate-400">
                    Vérification des conflits…
                  </div>
                )}
                {conflictState.has_hard_conflict && (
                  <div
                    data-testid="admin-stay-detail-conflict-hard"
                    className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-100"
                  >
                    <div className="font-medium">⛔ Conflit bloquant : un séjour confirmé occupe déjà cette période.</div>
                    <ul className="mt-1 list-disc pl-4 space-y-0.5">
                      {conflictState.hard.map((c) => (
                        <li key={c.stay_id}>
                          {formatDateEU(c.start_date)} → {formatDateEU(c.end_date)}
                          {c.tenant_name ? ` — ${c.tenant_name}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {conflictState.has_soft_conflict && !conflictState.has_hard_conflict && (
                  <div
                    data-testid="admin-stay-detail-conflict-soft"
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100"
                  >
                    <div className="font-medium">⚠️ Chevauchement iCal externe (non bloquant).</div>
                    <ul className="mt-1 list-disc pl-4 space-y-0.5">
                      {conflictState.soft.map((c) => (
                        <li key={c.stay_id}>
                          {formatDateEU(c.start_date)} → {formatDateEU(c.end_date)}
                          {c.ical_source ? ` — ${c.ical_source}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* ── Informations locataire éditables ─────────────────────── */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-100">Informations locataire</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-tenant-name-label" className="text-xs text-slate-300">Nom</Label>
                    <Input
                      data-testid="admin-stay-detail-tenant-name-input"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.tenant_name}
                      onChange={(e) => setDetailForm((f) => ({ ...f, tenant_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-tenant-email-label" className="text-xs text-slate-300">Email</Label>
                    <Input
                      data-testid="admin-stay-detail-tenant-email-input"
                      type="email"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.tenant_email}
                      onChange={(e) => setDetailForm((f) => ({ ...f, tenant_email: e.target.value }))}
                    />
                    <div data-testid="admin-stay-detail-tenant-email-hint" className="text-xs text-amber-300">
                      📧 Vivement conseillé — permet l&apos;envoi automatique des confirmations et informations au locataire.
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-tenant-phone-label" className="text-xs text-slate-300">Téléphone</Label>
                    <Input
                      data-testid="admin-stay-detail-tenant-phone-input"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.tenant_phone}
                      onChange={(e) => setDetailForm((f) => ({ ...f, tenant_phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-tenant-nationality-label" className="text-xs text-slate-300">Nationalité</Label>
                    <Input
                      data-testid="admin-stay-detail-tenant-nationality-input"
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.tenant_nationality}
                      onChange={(e) => setDetailForm((f) => ({ ...f, tenant_nationality: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-tenant-people-label" className="text-xs text-slate-300">Nombre de personnes</Label>
                    <Input
                      data-testid="admin-stay-detail-tenant-people-input"
                      type="number"
                      min={1}
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.tenant_people_count}
                      onChange={(e) => setDetailForm((f) => ({ ...f, tenant_people_count: Math.max(1, Number(e.target.value || 1)) }))}
                    />
                    {!peopleCountValid && (
                      <div data-testid="admin-stay-detail-people-invalid" className="text-xs text-rose-300">
                        Le nombre de personnes doit être supérieur à 0.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Formulaire Services éditables ─────────────────────── */}
              <fieldset data-testid="admin-stay-detail-services-fieldset" className="space-y-3">
                <div className="text-sm font-medium text-slate-100">Services</div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.05] px-3 py-2 cursor-pointer">
                    <span className="text-sm text-slate-100">🧹 Ménage</span>
                    <Checkbox
                      data-testid="admin-stay-detail-cleaning"
                      checked={!!detailForm.cleaning}
                      onCheckedChange={(v) => setDetailForm((f) => ({ ...f, cleaning: !!v }))}
                    />
                  </label>

                  <label className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.05] px-3 py-2 cursor-pointer">
                    <span className="text-sm text-slate-100">🧽 Dégraissage</span>
                    <Checkbox
                      data-testid="admin-stay-detail-degreasing"
                      checked={!!detailForm.degreasing}
                      onCheckedChange={(v) => setDetailForm((f) => ({ ...f, degreasing: !!v }))}
                    />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-kits-simple-label" className="text-xs text-slate-300">🛏️ Kits simple</Label>
                    <Input
                      data-testid="admin-stay-detail-kits-simple"
                      type="number"
                      min={0}
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.linen_kits_simple}
                      onChange={(e) => setKitSimple(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-kits-double-label" className="text-xs text-slate-300">🛏️ Kits double</Label>
                    <Input
                      data-testid="admin-stay-detail-kits-double"
                      type="number"
                      min={0}
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.linen_kits_double}
                      onChange={(e) => setKitDouble(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-beds-simple-label" className="text-xs text-slate-300">🛌 Lits faits simple</Label>
                    <Input
                      data-testid="admin-stay-detail-beds-simple"
                      type="number"
                      min={0}
                      max={detailForm.linen_kits_simple || 0}
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.beds_made_simple}
                      onChange={(e) => setDetailForm((f) => ({ ...f, beds_made_simple: Math.min(Math.max(0, Number(e.target.value || 0)), Number(f.linen_kits_simple || 0)) }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-beds-double-label" className="text-xs text-slate-300">🛌 Lits faits double</Label>
                    <Input
                      data-testid="admin-stay-detail-beds-double"
                      type="number"
                      min={0}
                      max={detailForm.linen_kits_double || 0}
                      className="border-white/10 bg-white/[0.08]"
                      value={detailForm.beds_made_double}
                      onChange={(e) => setDetailForm((f) => ({ ...f, beds_made_double: Math.min(Math.max(0, Number(e.target.value || 0)), Number(f.linen_kits_double || 0)) }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.05] px-3 py-2 cursor-pointer">
                    <span className="text-sm text-slate-100">🧴 Kit hygiène</span>
                    <Checkbox
                      data-testid="admin-stay-detail-hygiene"
                      checked={!!detailForm.hygiene_kit}
                      onCheckedChange={(v) => setDetailForm((f) => ({ ...f, hygiene_kit: !!v }))}
                    />
                  </label>

                  <div className="space-y-1">
                    <Label data-testid="admin-stay-detail-basket-label" className="text-xs text-slate-300">🎁 Panier d&apos;accueil</Label>
                    <select
                      data-testid="admin-stay-detail-basket"
                      className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                      value={detailForm.welcome_basket}
                      onChange={(e) => setDetailForm((f) => ({ ...f, welcome_basket: e.target.value }))}
                    >
                      <option value="none">Aucun</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-xs text-slate-300">👶 Puériculture</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "crib", label: "Lit bébé" },
                      { key: "high_chair", label: "Chaise haute" },
                      { key: "bath", label: "Baignoire" },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.05] px-3 py-2 cursor-pointer">
                        <span className="text-sm text-slate-100">{label}</span>
                        <Checkbox
                          data-testid={`admin-stay-detail-puericulture-${key}`}
                          checked={(detailForm.puericulture_items || []).includes(key)}
                          onCheckedChange={(v) => togglePuericulture(key, !!v)}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label data-testid="admin-stay-detail-notes-label" className="text-xs text-slate-300">📝 Notes planning</Label>
                  <Textarea
                    data-testid="admin-stay-detail-notes"
                    className="border-white/10 bg-white/[0.08] min-h-[80px]"
                    value={detailForm.notes_planning}
                    onChange={(e) => setDetailForm((f) => ({ ...f, notes_planning: e.target.value }))}
                    placeholder="Consignes pour la préparation terrain (ménage, kits, arrivée…)"
                  />
                </div>

                <div className="space-y-1">
                  <Label data-testid="admin-stay-detail-notes-facturation-label" className="text-xs text-slate-300 flex items-center justify-between">
                    <span>💰 Notes facturation</span>
                    <button
                      type="button"
                      data-testid="admin-stay-detail-notes-facturation-suggest"
                      className="text-[11px] text-amber-300 hover:text-amber-200 underline underline-offset-2"
                      onClick={() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const suggestion = `[${today}] Modification effectuée après facturation existante — à vérifier à la prochaine facture.`;
                        setDetailForm((f) => {
                          const current = (f.notes_facturation || "").trim();
                          if (!current) return { ...f, notes_facturation: suggestion };
                          if (current.includes(suggestion)) return f;
                          return { ...f, notes_facturation: `${current}\n${suggestion}` };
                        });
                      }}
                    >
                      + Insérer la note &quot;modif après facturation&quot;
                    </button>
                  </Label>
                  <Textarea
                    data-testid="admin-stay-detail-notes-facturation"
                    className="border-white/10 bg-white/[0.08] min-h-[80px]"
                    value={detailForm.notes_facturation}
                    onChange={(e) => setDetailForm((f) => ({ ...f, notes_facturation: e.target.value }))}
                    placeholder="Suivi facturation & régularisation (ajouts post-facture, écarts, remises à appliquer…)"
                  />
                  <p className="text-[11px] text-slate-400">
                    Distinct des notes planning. Visible dans le journal via l&apos;icône 💰 sur les séjours à régulariser.
                  </p>
                </div>
              </fieldset>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              data-testid="admin-stay-detail-cancel-stay-button"
              variant="destructive"
              className="rounded-full bg-rose-500 text-white hover:bg-rose-400"
              onClick={openCancelConfirm}
              disabled={detailSaving || cancelling}
              title="Annuler définitivement ce séjour (soft-cancel)"
            >
              🗑️ Annuler ce séjour
            </Button>
            <div className="flex gap-2">
              <Button
                data-testid="admin-stay-detail-cancel-button"
                variant="outline"
                className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
                onClick={closeDetail}
                disabled={detailSaving}
              >
                Annuler
              </Button>
              <Button
                data-testid="admin-stay-detail-save-button"
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                onClick={saveDetail}
                disabled={!canSave}
              >
                {detailSaving ? "Enregistrement…" : "Enregistrer les modifications"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AJOUT D — Dialog de confirmation "Envoyer un email au propriétaire ?" */}
      <Dialog
        open={!!emailPromptRow}
        onOpenChange={(o) => { if (!o && !detailSaving) setEmailPromptRow(null); }}
      >
        <DialogContent
          data-testid="admin-stay-email-prompt-dialog"
          className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[440px]"
        >
          <DialogHeader>
            <DialogTitle data-testid="admin-stay-email-prompt-title" className="text-slate-100">
              Envoyer un email de confirmation au propriétaire ?
            </DialogTitle>
          </DialogHeader>
          <p data-testid="admin-stay-email-prompt-desc" className="text-sm text-slate-300">
            Un email récapitulatif (dates et prestations avant/après) sera envoyé dans la langue du propriétaire.
          </p>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              data-testid="admin-stay-email-prompt-no"
              variant="outline"
              className="rounded-full border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
              disabled={detailSaving}
              onClick={() => performSave(false)}
            >
              Non, modification silencieuse
            </Button>
            <Button
              data-testid="admin-stay-email-prompt-yes"
              className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
              disabled={detailSaving}
              onClick={() => performSave(true)}
            >
              {detailSaving ? "Envoi…" : "Oui, envoyer l'email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AJOUT E — AlertDialog de confirmation d'annulation directe d'un séjour */}
      <AlertDialog
        open={!!cancelConfirmRow}
        onOpenChange={(o) => { if (!o && !cancelling) setCancelConfirmRow(null); }}
      >
        <AlertDialogContent
          data-testid="admin-stay-cancel-confirm-dialog"
          className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[500px]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="admin-stay-cancel-confirm-title" className="text-slate-100">
              Annuler définitivement ce séjour ?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="admin-stay-cancel-confirm-desc" className="text-slate-300 space-y-2">
              <span className="block">
                Le séjour du{" "}
                <b>{formatDateEU(cancelConfirmRow?.start_date)}</b> au{" "}
                <b>{formatDateEU(cancelConfirmRow?.end_date)}</b>
                {cancelConfirmRow?.tenant?.name ? <> — <b>{cancelConfirmRow.tenant.name}</b></> : null}
                {" "}sera marqué comme annulé et disparaîtra des plannings actifs (Travail, Journal) ainsi que des futures facturations.
              </span>
              {cancelConfirmRow?.is_invoiced && (
                <span
                  data-testid="admin-stay-cancel-confirm-invoice-warning"
                  className="block rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100"
                >
                  ⚠️ Ce séjour a une facture émise — l&apos;annulation n&apos;affecte pas la facture existante.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="admin-stay-cancel-confirm-no"
              className="rounded-full border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
              disabled={cancelling}
            >
              Non, revenir
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-stay-cancel-confirm-yes"
              className="rounded-full bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-50"
              disabled={cancelling}
              onClick={(e) => {
                e.preventDefault();
                setCancelEmailPromptRow(cancelConfirmRow);
                setCancelConfirmRow(null);
              }}
            >
              Oui, annuler le séjour
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AJOUT E — Dialog étape 2 : "Envoyer un email d'annulation au propriétaire ?" */}
      <Dialog
        open={!!cancelEmailPromptRow}
        onOpenChange={(o) => { if (!o && !cancelling) setCancelEmailPromptRow(null); }}
      >
        <DialogContent
          data-testid="admin-stay-cancel-email-prompt-dialog"
          className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[440px]"
        >
          <DialogHeader>
            <DialogTitle data-testid="admin-stay-cancel-email-prompt-title" className="text-slate-100">
              Envoyer un email d&apos;annulation au propriétaire ?
            </DialogTitle>
          </DialogHeader>
          <p data-testid="admin-stay-cancel-email-prompt-desc" className="text-sm text-slate-300">
            Un email d&apos;annulation (dates du séjour concerné) sera envoyé dans la langue du propriétaire.
          </p>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              data-testid="admin-stay-cancel-email-prompt-no"
              variant="outline"
              className="rounded-full border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
              disabled={cancelling}
              onClick={() => performCancel(false)}
            >
              Non, annulation silencieuse
            </Button>
            <Button
              data-testid="admin-stay-cancel-email-prompt-yes"
              className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
              disabled={cancelling}
              onClick={() => performCancel(true)}
            >
              {cancelling ? "Envoi…" : "Oui, envoyer l'email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
